/**
 * Theme copy of src/lib/ndef-record-size.ts and the value rules of
 * src/lib/store-line-encoding.ts (#1119, epic #1105 §4.2). It only drives the
 * "fits on the chip" hint; the payment-time server check decides. It must give
 * the same answers as the TypeScript module: tests/nfc-encoding-size-parity
 * runs both over the same vectors. `keysPerUnit` is the copy of
 * readyMadePackUnits() in src/lib/store-line-keys.ts (#1435).
 */
(function (root) {
  'use strict';

  var MAX_VALUE_LENGTH = 2048;
  var CONTROL_CHARS = /[\u0000-\u001f\u007f]/;

  function utf8Length(text) {
    return new TextEncoder().encode(text).length;
  }

  /** Bytes in one unchunked NDEF URI ('link') or Text ('text') record. */
  function estimateNdefRecordBytes(kind, value) {
    var payloadBytes;
    if (kind === 'link') {
      var prefixes = ['https://www.', 'http://www.', 'https://', 'http://'];
      var prefix = null;
      for (var i = 0; i < prefixes.length; i++) {
        if (value.indexOf(prefixes[i]) === 0) { prefix = prefixes[i]; break; }
      }
      payloadBytes = 1 + utf8Length(prefix ? value.slice(prefix.length) : value);
    } else {
      payloadBytes = 1 + 2 + utf8Length(value);
    }
    return 1 + 1 + (payloadBytes <= 255 ? 1 : 4) + 1 + payloadBytes;
  }

  /** 'link' for an http(s) URL with a host and no credentials, else 'text'. */
  function valueKind(value) {
    if (!/^https?:\/\/\S+$/i.test(value)) return 'text';
    try {
      var url = new URL(value);
      return url.hostname && !url.username && !url.password ? 'link' : 'text';
    } catch (e) {
      return 'text';
    }
  }

  /**
   * @returns {{ ok: boolean, reason: string|null, bytes: number }}
   * reason: 'empty' | 'invalid' | 'too_long' | null
   */
  function checkValue(rawValue, capacityBytes) {
    var value = String(rawValue == null ? '' : rawValue).trim();
    if (!value) return { ok: false, reason: 'empty', bytes: 0 };
    if (value.length > MAX_VALUE_LENGTH || CONTROL_CHARS.test(value)) {
      return { ok: false, reason: 'invalid', bytes: 0 };
    }
    var bytes = estimateNdefRecordBytes(valueKind(value), value);
    if (bytes > capacityBytes) return { ok: false, reason: 'too_long', bytes: bytes };
    return { ok: true, reason: null, bytes: bytes };
  }

  var PACK_COUNT = /\b(\d+)\s*-?\s*packs?\b|\bpacks?\s*(?:of\s*)?(\d+)\b/i;

  /**
   * Keys in one Shopify unit of a ready-made variant: N for an "N-Pack" /
   * "Pack of N" option, otherwise 1. Takes a variant title ("NTAG216 / Basic
   * 10-Pack") or the pack name nfc-encoding-attrs.liquid emits ("Basic 10-Pack").
   */
  function keysPerUnit(variantTitle) {
    var pack = String(variantTitle == null ? '' : variantTitle)
      .split('/')
      .map(function (segment) { return segment.trim(); })
      .filter(function (segment) { return segment && /pack/i.test(segment); })
      .join(' · ');
    var match = pack.match(PACK_COUNT);
    var units = match ? Number(match[1] || match[2]) : NaN;
    return units > 0 && units % 1 === 0 ? units : 1;
  }

  /**
   * MIFARE Classic is encoded by hand from the customer's sector data file, never
   * from a typed link or text (#1472). Same rule as isSectorFileChip() in
   * src/lib/chip-encoding-limits.ts.
   */
  function isSectorFileChip(chipName) {
    return /mifare\W*classic/i.test(String(chipName == null ? '' : chipName));
  }

  var api = {
    estimateNdefRecordBytes: estimateNdefRecordBytes,
    valueKind: valueKind,
    checkValue: checkValue,
    keysPerUnit: keysPerUnit,
    isSectorFileChip: isSectorFileChip
  };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else root.NfckeyEncodingSize = api;
})(typeof window !== 'undefined' ? window : globalThis);
